/**
 * Razorpay Subscription Service
 * 
 * Handles subscription payments via Firebase Cloud Functions
 * 
 * FLOW:
 * 1. User clicks "Upgrade to Pro"
 * 2. Call createSubscription Cloud Function
 * 3. Open Razorpay Checkout popup
 * 4. On success, call activateRazorpaySubscription to verify and activate
 * 5. User sees success immediately
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase-config';

/**
 * Load Razorpay checkout script
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Create a subscription for the user
 * @param {string} billingCycle - 'monthly' or 'yearly'
 * @param {string} region - 'india' (rupee plans) or 'international' (US dollar plans)
 * @returns {Promise<{success: boolean, subscriptionId?: string, error?: string}>}
 */
export const createSubscription = async (billingCycle, region = 'india') => {
  try {
    const createSubscriptionFn = httpsCallable(functions, 'createSubscription');
    
    const result = await createSubscriptionFn({ billingCycle, region });
    
    if (result.data.success && result.data.subscriptionId) {
      return {
        success: true,
        subscriptionId: result.data.subscriptionId,
        shortUrl: result.data.shortUrl,
        billingCycle: result.data.billingCycle,
        amount: result.data.amount,
        keyId: result.data.keyId,
        userName: result.data.userName,
        userEmail: result.data.userEmail
      };
    } else {
      return {
        success: false,
        error: 'Failed to create subscription'
      };
    }
  } catch (error) {
    console.error('Error creating subscription:', error);
    return {
      success: false,
      error: error.message || 'Failed to create subscription'
    };
  }
};

/**
 * Open Razorpay Checkout popup for subscription payment
 * @param {Object} options - Subscription details from createSubscription
 * @param {Function} onSuccess - Callback on successful payment
 * @param {Function} onError - Callback on payment error
 * @param {Function} onClose - Callback when popup is closed
 */
export const openRazorpayCheckout = (options, onSuccess, onError, onClose) => {
  const { subscriptionId, keyId, userName, userEmail, billingCycle, amount } = options;

  const rzpOptions = {
    key: keyId,
    subscription_id: subscriptionId,
    name: 'SoapLab',
    description: `Pro ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`,
    prefill: {
      name: userName || '',
      email: userEmail || ''
    },
    theme: {
      color: '#6366f1'
    },
    handler: function(response) {
      // Payment successful - response contains:
      // razorpay_payment_id, razorpay_subscription_id, razorpay_signature
      onSuccess({
        paymentId: response.razorpay_payment_id,
        subscriptionId: response.razorpay_subscription_id,
        signature: response.razorpay_signature,
        billingCycle: billingCycle
      });
    },
    modal: {
      ondismiss: function() {
        if (onClose) onClose();
      }
    }
  };

  const rzp = new window.Razorpay(rzpOptions);
  
  rzp.on('payment.failed', function(response) {
    onError({
      code: response.error.code,
      description: response.error.description,
      reason: response.error.reason
    });
  });

  rzp.open();
};

/**
 * Verify and activate Razorpay subscription after successful payment
 * @param {Object} paymentData - Payment data from Razorpay checkout
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const activateRazorpaySubscription = async (paymentData) => {
  try {
    const activateFn = httpsCallable(functions, 'activateRazorpaySubscription');
    
    const result = await activateFn({
      subscriptionId: paymentData.subscriptionId,
      paymentId: paymentData.paymentId,
      signature: paymentData.signature,
      billingCycle: paymentData.billingCycle
    });
    
    return {
      success: result.data.success,
      message: result.data.message
    };
  } catch (error) {
    console.error('Error activating subscription:', error);
    return {
      success: false,
      error: error.message || 'Failed to activate subscription'
    };
  }
};

/**
 * Cancel the user's subscription
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const cancelSubscription = async () => {
  try {
    const cancelSubscriptionFn = httpsCallable(functions, 'cancelSubscription');
    
    const result = await cancelSubscriptionFn();
    
    return {
      success: result.data.success,
      message: result.data.message
    };
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    return {
      success: false,
      error: error.message || 'Failed to cancel subscription'
    };
  }
};

/**
 * Get the user's subscription status
 * @returns {Promise<{plan: string, status: string, billingCycle: string, endDate: Date}>}
 */
export const getSubscriptionStatus = async () => {
  try {
    const getStatusFn = httpsCallable(functions, 'getSubscriptionStatus');
    
    const result = await getStatusFn();
    
    return {
      plan: result.data.plan,
      subscriptionStatus: result.data.subscriptionStatus,
      billingCycle: result.data.billingCycle,
      subscriptionEndDate: result.data.subscriptionEndDate ? new Date(result.data.subscriptionEndDate) : null,
      subscriptionId: result.data.subscriptionId
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return {
      plan: 'free',
      subscriptionStatus: null,
      billingCycle: null,
      subscriptionEndDate: null,
      subscriptionId: null
    };
  }
};

/**
 * Redirect user to Razorpay payment page (legacy - use openRazorpayCheckout instead)
 * @param {string} shortUrl - Razorpay subscription short URL
 */
export const redirectToPayment = (shortUrl) => {
  window.location.href = shortUrl;
};
